import { getBackendUrl } from '../../config/environment';
/**
 * ModuleContentEditor - Éditeur complet du contenu d'un module
 * Permet d'éditer : métadonnées, leçons, et scénarios IA
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  BookOpen, 
  Brain,
  FileText
} from 'lucide-react';
import MdEditor from 'react-markdown-editor-lite';
import ReactMarkdown from 'react-markdown';
import 'react-markdown-editor-lite/lib/index.css';

const BACKEND_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:8001' 
  : getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ModuleContentEditor = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [module, setModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [scenarios, setScenarios] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadModuleContent();
  }, [user, moduleId, navigate]);

  const loadModuleContent = async () => {
    setLoading(true);
    try {
      // Load module with all content
      const response = await axios.get(`${API}/modules-v2/${moduleId}/content`);
      const data = response.data;
      
      setModule(data.module);
      setLessons(data.lessons || []);
      setScenarios(data.scenarios || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement du contenu');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Save lessons
      for (const lesson of lessons) {
        if (lesson.id) {
          // Update existing lesson
          await axios.put(`${API}/lessons/${lesson.id}`, {
            title: lesson.title,
            content: lesson.content,
            module_id: parseInt(moduleId),
            order: lesson.order || 1
          }, { headers });
        } else {
          // Create new lesson
          await axios.post(`${API}/lessons`, {
            title: lesson.title,
            content: lesson.content,
            module_id: parseInt(moduleId),
            order: lesson.order || 1
          }, { headers });
        }
      }

      // Save scenarios
      for (const scenario of scenarios) {
        // Clean and validate difficulty_levels
        const cleanDifficultyLevels = (scenario.difficulty_levels || []).map(level => ({
          level: level.level || '',
          description: level.description || '',
          coaching_approach: level.coaching_approach || ''
        }));

        const scenarioData = {
          title: scenario.title || '',
          context: scenario.context || '',
          learning_objectives: Array.isArray(scenario.learning_objectives) 
            ? scenario.learning_objectives.filter(obj => obj && obj.trim())
            : [],
          module_id: parseInt(moduleId),
          difficulty_levels: cleanDifficultyLevels
        };

        if (scenario.id) {
          // Update existing scenario
          await axios.put(`${API}/scenarios/${scenario.id}`, scenarioData, { headers });
        } else {
          // Create new scenario
          await axios.post(`${API}/scenarios`, scenarioData, { headers });
        }
      }

      toast.success('Contenu sauvegardé avec succès');
      await loadModuleContent();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Lesson management
  const handleAddLesson = () => {
    setLessons([...lessons, {
      title: '',
      content: '',
      order: lessons.length + 1,
      module_id: parseInt(moduleId)
    }]);
  };

  const handleUpdateLesson = (index, field, value) => {
    const newLessons = [...lessons];
    newLessons[index] = { ...newLessons[index], [field]: value };
    setLessons(newLessons);
  };

  const handleDeleteLesson = async (index) => {
    const lesson = lessons[index];
    
    if (lesson.id) {
      if (!window.confirm('Supprimer cette leçon ?')) return;
      
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        await axios.delete(`${API}/lessons/${lesson.id}`, { headers });
        toast.success('Leçon supprimée');
      } catch (error) {
        toast.error('Erreur lors de la suppression');
        return;
      }
    }
    
    setLessons(lessons.filter((_, i) => i !== index));
  };

  // Scenario management
  const handleAddScenario = () => {
    setScenarios([...scenarios, {
      title: '',
      context: '',
      learning_objectives: [],
      module_id: parseInt(moduleId),
      difficulty_levels: [
        { level: 'debutant', description: '', coaching_approach: '' },
        { level: 'intermediaire', description: '', coaching_approach: '' },
        { level: 'expert', description: '', coaching_approach: '' }
      ]
    }]);
  };

  const handleUpdateScenario = (index, field, value) => {
    const newScenarios = [...scenarios];
    newScenarios[index] = { ...newScenarios[index], [field]: value };
    setScenarios(newScenarios);
  };

  const handleUpdateScenarioDifficulty = (scenarioIndex, difficultyIndex, field, value) => {
    const newScenarios = [...scenarios];
    if (!newScenarios[scenarioIndex].difficulty_levels) {
      newScenarios[scenarioIndex].difficulty_levels = [];
    }
    if (!newScenarios[scenarioIndex].difficulty_levels[difficultyIndex]) {
      newScenarios[scenarioIndex].difficulty_levels[difficultyIndex] = {};
    }
    newScenarios[scenarioIndex].difficulty_levels[difficultyIndex][field] = value;
    setScenarios(newScenarios);
  };

  const handleDeleteScenario = async (index) => {
    const scenario = scenarios[index];
    
    if (scenario.id) {
      if (!window.confirm('Supprimer ce scénario ?')) return;
      
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        await axios.delete(`${API}/scenarios/${scenario.id}`, { headers });
        toast.success('Scénario supprimé');
      } catch (error) {
        toast.error('Erreur lors de la suppression');
        return;
      }
    }
    
    setScenarios(scenarios.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du contenu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/admin')}
                variant="ghost"
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour admin
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {module?.icon} {module?.title}
                </h1>
                <p className="text-sm text-gray-600">Édition du contenu complet</p>
              </div>
            </div>
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder tout'}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="lessons" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="lessons" className="gap-2">
              <BookOpen className="w-4 h-4" />
              Leçons ({lessons.length})
            </TabsTrigger>
            <TabsTrigger value="scenarios" className="gap-2">
              <Brain className="w-4 h-4" />
              Scénarios IA ({scenarios.length})
            </TabsTrigger>
          </TabsList>

          {/* LESSONS TAB */}
          <TabsContent value="lessons" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Leçons du module</h2>
              <Button onClick={handleAddLesson} className="gap-2">
                <Plus className="w-4 h-4" />
                Ajouter une leçon
              </Button>
            </div>

            {lessons.map((lesson, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Leçon {index + 1}</CardTitle>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteLesson(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor={`lesson-title-${index}`}>Titre de la leçon</Label>
                    <Input
                      id={`lesson-title-${index}`}
                      value={lesson.title}
                      onChange={(e) => handleUpdateLesson(index, 'title', e.target.value)}
                      placeholder="ex: Introduction au leadership"
                    />
                  </div>

                  <div>
                    <Label htmlFor={`lesson-content-${index}`}>Contenu (Markdown supporté)</Label>
                    <div className="border border-gray-200 rounded-md overflow-hidden" style={{ backgroundColor: '#fafafa' }}>
                      <MdEditor
                        value={lesson.content || ''}
                        style={{ height: '500px' }}
                        renderHTML={text => <ReactMarkdown>{text}</ReactMarkdown>}
                        onChange={({ text }) => handleUpdateLesson(index, 'content', text)}
                        view={{ menu: true, md: true, html: true }}
                        canView={{ menu: true, md: true, html: true, both: true, fullScreen: true }}
                        config={{
                          view: {
                            menu: true,
                            md: true,
                            html: true
                          },
                          imageUrl: '',
                          syncScrollMode: ['leftFollowRight', 'rightFollowLeft']
                        }}
                        plugins={[
                          'header',
                          'font-bold',
                          'font-italic',
                          'font-underline',
                          'font-strikethrough',
                          'list-unordered',
                          'list-ordered',
                          'block-quote',
                          'block-wrap',
                          'block-code-inline',
                          'block-code-block',
                          'table',
                          'image',
                          'link',
                          'clear',
                          'logger',
                          'mode-toggle',
                          'full-screen'
                        ]}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 flex items-center">
                      💡 <span className="ml-1">Astuce : utilisez Markdown pour structurer vos leçons (titres, listes, citations, mise en valeur).</span>
                    </p>
                  </div>

                  <div>
                    <Label htmlFor={`lesson-order-${index}`}>Ordre</Label>
                    <Input
                      id={`lesson-order-${index}`}
                      type="number"
                      value={lesson.order || index + 1}
                      onChange={(e) => handleUpdateLesson(index, 'order', parseInt(e.target.value))}
                      min="1"
                      className="w-32"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}

            {lessons.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Aucune leçon pour ce module</p>
                  <Button onClick={handleAddLesson}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter la première leçon
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SCENARIOS TAB */}
          <TabsContent value="scenarios" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Scénarios IA du module</h2>
              <Button onClick={handleAddScenario} className="gap-2">
                <Plus className="w-4 h-4" />
                Ajouter un scénario
              </Button>
            </div>

            {scenarios.map((scenario, scenarioIndex) => (
              <Card key={scenarioIndex}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Scénario {scenarioIndex + 1}</CardTitle>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteScenario(scenarioIndex)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor={`scenario-title-${scenarioIndex}`}>Titre du scénario</Label>
                    <Input
                      id={`scenario-title-${scenarioIndex}`}
                      value={scenario.title}
                      onChange={(e) => handleUpdateScenario(scenarioIndex, 'title', e.target.value)}
                      placeholder="ex: Gérer un conflit d'équipe"
                    />
                  </div>

                  <div>
                    <Label htmlFor={`scenario-context-${scenarioIndex}`}>Contexte du scénario</Label>
                    <Textarea
                      id={`scenario-context-${scenarioIndex}`}
                      value={scenario.context}
                      onChange={(e) => handleUpdateScenario(scenarioIndex, 'context', e.target.value)}
                      rows={6}
                      placeholder="Décrivez la situation, le contexte, les personnages..."
                    />
                  </div>

                  <div>
                    <Label htmlFor={`scenario-objectives-${scenarioIndex}`}>Objectifs pédagogiques</Label>
                    <Textarea
                      id={`scenario-objectives-${scenarioIndex}`}
                      value={Array.isArray(scenario.learning_objectives) 
                        ? scenario.learning_objectives.join('\n') 
                        : scenario.learning_objectives || ''
                      }
                      onChange={(e) => handleUpdateScenario(
                        scenarioIndex, 
                        'learning_objectives', 
                        e.target.value.split('\n').filter(o => o.trim())
                      )}
                      rows={4}
                      placeholder="Un objectif par ligne&#10;ex: Pratiquer l'écoute active&#10;Gérer les émotions"
                    />
                    <p className="text-xs text-gray-500 mt-1">Un objectif par ligne</p>
                  </div>

                  {/* Difficulty Levels */}
                  <div className="space-y-4 border-t pt-4">
                    <h3 className="font-semibold">Niveaux de difficulté</h3>
                    
                    {['debutant', 'intermediaire', 'expert'].map((level, diffIndex) => {
                      const difficulty = scenario.difficulty_levels?.[diffIndex] || {};
                      
                      return (
                        <Card key={level} className="bg-gray-50">
                          <CardHeader>
                            <CardTitle className="text-base capitalize">{level}</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div>
                              <Label>Description du niveau</Label>
                              <Textarea
                                value={difficulty.description || ''}
                                onChange={(e) => handleUpdateScenarioDifficulty(
                                  scenarioIndex, 
                                  diffIndex, 
                                  'description', 
                                  e.target.value
                                )}
                                rows={3}
                                placeholder={`Décrivez les spécificités du niveau ${level}...`}
                              />
                            </div>

                            <div>
                              <Label>Approche de coaching</Label>
                              <Textarea
                                value={difficulty.coaching_approach || ''}
                                onChange={(e) => handleUpdateScenarioDifficulty(
                                  scenarioIndex, 
                                  diffIndex, 
                                  'coaching_approach', 
                                  e.target.value
                                )}
                                rows={3}
                                placeholder="Comment l'IA doit coacher pour ce niveau..."
                              />
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}

            {scenarios.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Aucun scénario IA pour ce module</p>
                  <Button onClick={handleAddScenario}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter le premier scénario
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ModuleContentEditor;
